import { FC } from 'react'

import { Task } from '@droidmap/project-service-contract'

import ConfirmationDialog from '../ConfirmationDialog'

interface TaskCancelConfirmationDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  task: Task | null
  isLoading?: boolean
  /** Set when the cancel request itself failed; shown in the dialog body. */
  error?: Error | null
}

/**
 * Confirm cancelling a running task's mapping job (droidmap#2982).
 *
 * The copy states both consequences up front, because neither is
 * recoverable: the work done so far is thrown away — a cancelled job keeps no
 * partial outputs — and the compute it has already used is still charged.
 */
const TaskCancelConfirmationDialog: FC<TaskCancelConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  task,
  isLoading = false,
  error,
}) => (
  <ConfirmationDialog
    title="Cancel job"
    description={
      <>
        Stop{' '}
        <strong className="font-semibold text-text">
          {task?.name || 'this task'}
        </strong>{' '}
        now? The work done so far is discarded and can&rsquo;t be resumed. The
        compute it has used up to this point is still charged.
      </>
    }
    isOpen={isOpen}
    onClose={onClose}
    onConfirm={() => onConfirm()}
    confirmText="Cancel job"
    cancelText="Keep running"
    variant="destructive"
    isLoading={isLoading}
    error={
      error ? (
        <div
          role="alert"
          className="rounded-md border border-danger/20 bg-danger/10 p-3 text-sm text-danger"
        >
          Couldn&rsquo;t cancel the job: {error.message}
        </div>
      ) : undefined
    }
  />
)

export default TaskCancelConfirmationDialog
